import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Container,
  CssBaseline,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  FamilyRestroom as FamilyIcon,
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  Login as LoginIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { buildApiUrl } from './config';
import Logo from './components/Logo';

const ParentLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [parents, setParents] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    axios.get(buildApiUrl('/data/parents.json'))
      .then(res => {
        setParents(res.data);
        
        // Check for troubleshoot credentials
        const troubleshootUsername = localStorage.getItem('troubleshoot_username');
        const troubleshootPassword = localStorage.getItem('troubleshoot_password');
        const troubleshootUserType = localStorage.getItem('troubleshoot_userType');
        
        if (troubleshootUsername && troubleshootPassword && troubleshootUserType === 'Parent') {
          setEmail(troubleshootUsername);
          setPassword(troubleshootPassword);
          
          // Clear troubleshoot credentials
          localStorage.removeItem('troubleshoot_username');
          localStorage.removeItem('troubleshoot_password');
          localStorage.removeItem('troubleshoot_userType');
          
          // Auto-login after a short delay
          setTimeout(() => {
            const parent = res.data?.find(p => p.email.toLowerCase() === troubleshootUsername.toLowerCase());
            if (parent && troubleshootPassword === 'Parent123') {
              localStorage.setItem("parentId", parent.id);
              localStorage.setItem("parentEmail", parent.email.toLowerCase());
              login("parent", "dummy-token");
            }
          }, 100);
        }
      })
      .catch(() => setParents([]));
  }, [login]);

  const handleSubmit = () => {
    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }

    setLoading(true);
    setError('');

    // Simulate network delay for better UX
    setTimeout(() => {
      const parent = parents.find(p => p.email.toLowerCase() === email.toLowerCase());
      if (parent && password === 'Parent123') {
        localStorage.setItem("parentId", parent.id);
        localStorage.setItem("parentEmail", parent.email.toLowerCase());
        login("parent", "dummy-token");
        const returnUrl = localStorage.getItem("docsReturnUrl");
        if (returnUrl) {
          localStorage.removeItem("docsReturnUrl");
          window.location.href = returnUrl;
          return;
        }
      } else {
        setError('Invalid credentials');
      }
      setLoading(false);
    }, 500);
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #4a148c 0%, #311b92 100%)', // Purple/Indigo gradient
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.1) 0%, rgba(0,0,0,0) 50%)',
          pointerEvents: 'none',
        }
      }}
    >
      <CssBaseline />
      <Container maxWidth="xs">
        <Paper
          elevation={24}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            borderRadius: 4,
            transition: 'transform 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-5px)'
            }
          }}
        >
          <Logo size={50} fontSize="1.5rem" />

          <Typography component="h1" variant="h4" sx={{ mb: 1, fontWeight: 700, color: '#311b92' }}>
            Parent Portal
          </Typography>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 4 }}>
            Stay connected with your child's progress
          </Typography>

          <Box component="form" noValidate sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#fff',
                  '&:hover fieldset': {
                    borderColor: '#4a148c',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#4a148c',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#4a148c',
                }
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#fff',
                  '&:hover fieldset': {
                    borderColor: '#4a148c',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#4a148c',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#4a148c',
                }
              }}
            />

            {error && (
              <Typography
                color="error"
                variant="body2"
                sx={{
                  mt: 2,
                  textAlign: 'center',
                  backgroundColor: 'rgba(211, 47, 47, 0.1)',
                  padding: 1,
                  borderRadius: 1
                }}
              >
                {error}
              </Typography>
            )}

            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              startIcon={!loading && <LoginIcon />}
              sx={{
                mt: 4,
                mb: 2,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 2,
                backgroundColor: '#4a148c',
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(74, 20, 140, 0.3)',
                '&:hover': {
                  backgroundColor: '#311b92',
                  boxShadow: '0 6px 16px rgba(74, 20, 140, 0.4)',
                },
              }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </Box>
        </Paper>
        <Typography variant="body2" color="white" align="center" sx={{ mt: 4, opacity: 0.8 }}>
          © {new Date().getFullYear()} School Management System
        </Typography>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Link to="/privacy-policy" style={{ color: 'white', opacity: 0.8, textDecoration: 'none', fontSize: '0.875rem' }}>
            Privacy Policy
          </Link>
        </Box>
      </Container>
    </Box>
  );
};

export default ParentLogin;
